import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Home, Target, Briefcase, Image, FileText, User, LayoutGrid } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useLocalStorageState } from '@/hooks/ai-agents/useLocalStorageState';
import NavItem from '@/components/layout/NavItem';
import { usePinnedAppsWithDetails } from '@/hooks/app-library/use-user-pinned-apps';
import { getIconByName } from '@/hooks/app-library/utils';
import { getActualRoute } from '@/utils/routeMapper';

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getPinnedAppDetails, isLoading } = usePinnedAppsWithDetails();
  const { isModalOpen, closeModal } = useLocalStorageState();

  // Custom breakpoint detection for 1024px (lg breakpoint)
  const [isBelow1024px, setIsBelow1024px] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 1024;
  });

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1023px)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setIsBelow1024px(e.matches);
    };

    mql.addEventListener('change', handleChange);
    setIsBelow1024px(mql.matches);

    return () => {
      mql.removeEventListener('change', handleChange);
    };
  }, []);

  // Handler to close mobile chat when any nav item is clicked
  const handleNavItemClick = () => {
    if (isModalOpen) {
      closeModal();
    }
  };

  // Get pinned apps details (now 4 apps)
  const pinnedApp1 = getPinnedAppDetails(1);
  const pinnedApp2 = getPinnedAppDetails(2);
  const pinnedApp3 = getPinnedAppDetails(3);
  const pinnedApp4 = getPinnedAppDetails(4);

  // Fallback nav items if pinned apps are not loaded (responsive naming)
  const fallbackNavItems = [
    {
      name: isBelow1024px ? 'Documents' : 'Document Generator',
      to: '/private-text-generator',
      icon: React.createElement(getIconByName('FileText'), { size: 20 })
    },
    {
      name: isBelow1024px ? 'Research' : 'Research Generator',
      to: '/private-research-generator',
      icon: React.createElement(getIconByName('Microscope'), { size: 20 })
    },
    {
      name: isBelow1024px ? 'Images' : 'Image Generator',
      to: '/private-image-generator',
      icon: React.createElement(getIconByName('Images'), { size: 20 })
    },
    {
      name: isBelow1024px ? 'Videos' : 'Video Generator',
      to: '/video-generator',
      icon: React.createElement(getIconByName('Clapperboard'), { size: 20 })
    }
  ];

  // Create nav items from pinned apps (now 4 apps)
  const createNavItems = () => {
    if (isLoading) {
      return fallbackNavItems;
    }

    const navItems = [];
    
    // Add all 4 pinned apps with responsive naming
    [pinnedApp1, pinnedApp2, pinnedApp3, pinnedApp4].forEach(app => {
      if (app) {
        // Use short name below 1024px, full name at 1024px and above
        const displayName = isBelow1024px 
          ? (app.short_display_name || app.display_name || app.name)
          : (app.display_name || app.name);
        
        navItems.push({
          name: displayName,
          to: getActualRoute(app.route),
          icon: React.createElement(getIconByName(app.icon_name), { size: 20 })
        });
      }
    });

    return navItems;
  };

  const navItems = createNavItems();

  return (
    <nav className="w-full">
      <ul className="flex justify-around items-center w-full">
        {/* All four navigation items */}
        {navItems.slice(0, 4).map((item) => (
          <li key={item.name}>
            <NavItem
              to={item.to}
              label={item.name}
              icon={item.icon}
              isActive={location.pathname.startsWith(item.to)}
              onClick={handleNavItemClick}
            />
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default MobileBottomNav;
